import React, { useState, useEffect } from "react";
import { useQuery, useLazyQuery } from "@apollo/react-hooks";
import { gql } from "apollo-boost";
import moment from "moment";
import XLSX from "xlsx";
import IconButton from "@material-ui/core/IconButton";

import ShoppingCartIcon from "@material-ui/icons/ShoppingCart";
import AttachMoneyIcon from "@material-ui/icons/AttachMoney";
import HourglassEmptyIcon from "@material-ui/icons/HourglassEmpty";
import BlockIcon from "@material-ui/icons/Block";
import RefreshIcon from "@material-ui/icons/Refresh";
import CloudDownloadIcon from "@material-ui/icons/CloudDownload";
import InfoOutlinedIcon from "@material-ui/icons/InfoOutlined";
import FileCopyIcon from "@material-ui/icons/FileCopy";
import CheckCircleOutlineIcon from "@material-ui/icons/CheckCircleOutline";

import ErrorPage from "commonComponents/errorPage/ErrorPage";
import OrderDetails from "shopAdminViews/orders/OrderDetails";
import DialogShell from "designV2/components/DialogShell";
import PageHeader from "designV2/components/PageHeader";
import PageActionButton from "designV2/components/PageActionButton";
import MetricCard from "designV2/components/MetricCard";
import SearchField from "designV2/components/SearchField";
import FilterDropdown from "designV2/components/FilterDropdown";
import NumberRangeFilter from "designV2/components/NumberRangeFilter";
import DateRangeFilter from "designV2/components/DateRangeFilter";
import ActiveFilterChipList from "designV2/components/ActiveFilterChipList";
import StatusBadge from "designV2/components/StatusBadge";
import EmptyState from "designV2/components/EmptyState";
import PaginationComponent from "designV2/components/Pagination";
import { MetricsSkeleton, TableSkeleton } from "designV2/components/Skeleton";
import "../superShops.css";

/* ------------------------------------------------------------------ *
 * GraphQL
 * ------------------------------------------------------------------ */

const ORDER_FILTERS = `
  shop: $shop
  status: $status
  finished: $finished
  passengerName: $passengerName
  passengerPhoneNumber: $passengerPhoneNumber
  finalPriceFrom: $finalPriceFrom
  finalPrice: $finalPrice
  commissionFrom: $commissionFrom
  commission: $commission
  createdAtFrom: $createdAtFrom
  createdAt: $createdAt
`;

const ORDER_VARIABLES = `
  $limit: Int
  $skip: Int
  $shop: ID
  $status: OrderStatus
  $finished: Boolean
  $passengerName: String
  $passengerPhoneNumber: String
  $finalPriceFrom: Float
  $finalPrice: Float
  $commissionFrom: Float
  $commission: Float
  $createdAtFrom: Date
  $createdAt: Date
  $createdAtSort: Int
  $finalPriceSort: Int
  $commissionSort: Int
`;

const GET_ORDERS = gql`
  query getOrdersByAdmin(${ORDER_VARIABLES}) {
    getOrdersByAdmin(
      pagination: { limit: $limit, skip: $skip }
      filters: { ${ORDER_FILTERS} }
      sort: {
        createdAt: $createdAtSort
        finalPrice: $finalPriceSort
        commission: $commissionSort
      }
    ) {
      _id
      status
      rejectedFor
      shipmentModel
      type
      finalPrice
      commission
      createdAt
      finished
      user {
        _id
        fullName
        phoneNumber
      }
      tracking {
        trackId
      }
      cart {
        products {
          quantity
          product {
            _id
            title
          }
        }
      }
    }
    getOrdersByAdminCount(filters: { ${ORDER_FILTERS} })
  }
`;

// Queue sizes for this shop across every order, not just the page.
const GET_ORDER_COUNTS = gql`
  query getShopOrderCounts($shop: ID) {
    total: getOrdersByAdminCount(filters: { shop: $shop })
    open: getOrdersByAdminCount(filters: { shop: $shop, finished: false })
    rejected: getOrdersByAdminCount(filters: { shop: $shop, status: REJECTED })
  }
`;

/* ------------------------------------------------------------------ *
 * Constants
 * ------------------------------------------------------------------ */

const STATUS_OPTIONS = [
  { value: "PENDING", label: "Pending" },
  { value: "PAID", label: "Paid" },
  { value: "PREPARING", label: "Preparing" },
  { value: "PREPARED", label: "Prepared" },
  { value: "SHIPPING", label: "Shipping" },
  { value: "DELIVERED", label: "Delivered" },
  { value: "REJECTED", label: "Rejected" },
  { value: "DELIVERY_NOT_ACCEPTED", label: "Delivery not accepted" },
  { value: "FINISHED_DUE_TO_NOT_PAYING", label: "Ended, not paid" },
];
const STATUS_LABELS = STATUS_OPTIONS.reduce((acc, o) => {
  acc[o.value] = o.label;
  return acc;
}, {});
const STATUS_TONES = {
  PENDING: "neutral",
  PAID: "info",
  PREPARING: "warning",
  PREPARED: "warning",
  SHIPPING: "info",
  DELIVERED: "success",
  REJECTED: "danger",
  DELIVERY_NOT_ACCEPTED: "danger",
  FINISHED_DUE_TO_NOT_PAYING: "danger",
};

const SHIPMENT_LABELS = { SPARK: "Spark delivery", SHOP: "Delivered by shop" };

const PROGRESS_OPTIONS = [
  { value: "", label: "Any progress" },
  { value: "false", label: "Open" },
  { value: "true", label: "Finished" },
];
const PROGRESS_LABELS = PROGRESS_OPTIONS.reduce((acc, o) => {
  acc[o.value] = o.label;
  return acc;
}, {});

const SORT_OPTIONS = [
  { value: "newest", label: "Newest first" },
  { value: "oldest", label: "Oldest first" },
  { value: "priceHigh", label: "Total high-low" },
  { value: "priceLow", label: "Total low-high" },
  { value: "commissionHigh", label: "Commission high-low" },
];

const FILTER_DEBOUNCE_MS = 350;

const looksLikePhone = (value) => /^[\d\s+()-]+$/.test(value);
const money = (value) => `$${Number(value || 0).toFixed(2)}`;

/* ------------------------------------------------------------------ *
 * Component
 * ------------------------------------------------------------------ */

const SuperOrder = ({ id: shopId, shopTitle }) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [progressFilter, setProgressFilter] = useState("");
  const [priceFrom, setPriceFrom] = useState("");
  const [priceTo, setPriceTo] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [sortOption, setSortOption] = useState("newest");

  const [pageCount, setPageCount] = useState(0);
  const [perPage, setPerPage] = useState(10);

  const [orderDetail, setOrderDetail] = useState(null);
  const [copiedId, setCopiedId] = useState("");
  const [exporting, setExporting] = useState(false);

  const [variables, setVariables] = useState({
    limit: perPage,
    skip: 0,
    shop: shopId,
    createdAtSort: -1,
  });

  useEffect(() => {
    const handler = setTimeout(() => {
      const term = searchTerm.trim();
      setVariables((v) => ({
        ...v,
        skip: 0,
        shop: shopId,
        passengerName: undefined,
        passengerPhoneNumber: undefined,
        ...(term
          ? looksLikePhone(term)
            ? { passengerPhoneNumber: term }
            : { passengerName: term }
          : {}),
        status: statusFilter || undefined,
        finished: progressFilter === "" ? undefined : progressFilter === "true",
        finalPriceFrom: priceFrom ? parseFloat(priceFrom) : undefined,
        finalPrice: priceTo ? parseFloat(priceTo) : undefined,
        createdAtFrom: dateFrom || undefined,
        createdAt: dateTo || undefined,
        finalPriceSort:
          sortOption === "priceHigh" ? -1 : sortOption === "priceLow" ? 1 : undefined,
        commissionSort: sortOption === "commissionHigh" ? -1 : undefined,
        createdAtSort:
          sortOption === "oldest" ? 1 : sortOption === "newest" ? -1 : undefined,
      }));
      setPageCount(0);
    }, FILTER_DEBOUNCE_MS);
    return () => clearTimeout(handler);
  }, [shopId, searchTerm, statusFilter, progressFilter, priceFrom, priceTo, dateFrom, dateTo, sortOption]);

  useEffect(() => {
    setVariables((v) => ({ ...v, limit: perPage, skip: perPage * pageCount }));
  }, [perPage, pageCount]);

  const { data, loading, error, refetch } = useQuery(GET_ORDERS, {
    variables,
    fetchPolicy: "network-only",
  });
  const { data: countsData, refetch: refetchCounts } = useQuery(GET_ORDER_COUNTS, {
    variables: { shop: shopId },
    fetchPolicy: "network-only",
    errorPolicy: "all",
  });
  const [fetchAllForExport, { data: exportData }] = useLazyQuery(GET_ORDERS, {
    fetchPolicy: "network-only",
  });

  const orders = (data && data.getOrdersByAdmin) || [];
  const totalCount = (data && data.getOrdersByAdminCount) || 0;
  const counts = countsData || {};
  const pageRevenue = orders.reduce((sum, o) => sum + (o.finalPrice || 0), 0);
  const pageCommission = orders.reduce((sum, o) => sum + (o.commission || 0), 0);

  /* ---------------- filter chips ---------------- */

  const activeFilterChips = [];
  if (searchTerm.trim()) activeFilterChips.push({ id: "search", label: `Customer: ${searchTerm}` });
  if (statusFilter) activeFilterChips.push({ id: "status", label: `Status: ${STATUS_LABELS[statusFilter]}` });
  if (progressFilter) activeFilterChips.push({ id: "progress", label: PROGRESS_LABELS[progressFilter] });
  if (priceFrom || priceTo) {
    activeFilterChips.push({ id: "price", label: `Total: $${priceFrom || "0"} to $${priceTo || "-"}` });
  }
  if (dateFrom || dateTo) {
    activeFilterChips.push({ id: "date", label: `Placed: ${dateFrom || "-"} to ${dateTo || "-"}` });
  }

  const clearFilter = (id) => {
    if (id === "search") setSearchTerm("");
    if (id === "status") setStatusFilter("");
    if (id === "progress") setProgressFilter("");
    if (id === "price") {
      setPriceFrom("");
      setPriceTo("");
    }
    if (id === "date") {
      setDateFrom("");
      setDateTo("");
    }
  };

  const clearAllFilters = () => {
    setSearchTerm("");
    setStatusFilter("");
    setProgressFilter("");
    setPriceFrom("");
    setPriceTo("");
    setDateFrom("");
    setDateTo("");
  };

  /* ---------------- export ---------------- */

  useEffect(() => {
    if (!exporting || !exportData) return;
    const rows = [
      ["Track ID", "Customer", "Phone", "Status", "Delivery", "Items", "Total", "Commission", "Placed"],
    ];
    (exportData.getOrdersByAdmin || []).forEach((o) => {
      rows.push([
        (o.tracking && o.tracking.trackId) || "-",
        (o.user && o.user.fullName) || "-",
        (o.user && o.user.phoneNumber) || "-",
        STATUS_LABELS[o.status] || o.status || "-",
        SHIPMENT_LABELS[o.shipmentModel] || o.shipmentModel || "-",
        ((o.cart && o.cart.products) || []).reduce((sum, p) => sum + (p.quantity || 0), 0),
        Number(o.finalPrice || 0).toFixed(2),
        Number(o.commission || 0).toFixed(2),
        moment(o.createdAt).format("MM/DD/YYYY HH:mm"),
      ]);
    });
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(rows), "Orders");
    XLSX.writeFile(wb, `${(shopTitle || "Shop").replace(/\s+/g, "-")}-Orders.xlsx`);
    setExporting(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [exportData]);

  const handleExport = () => {
    if (totalCount === 0 || exporting) return;
    setExporting(true);
    fetchAllForExport({ variables: { ...variables, skip: 0, limit: totalCount } });
  };

  const handleCopy = (value) => {
    if (!navigator.clipboard) return;
    navigator.clipboard.writeText(value).then(() => {
      setCopiedId(value);
      window.setTimeout(() => setCopiedId(""), 1500);
    });
  };

  /* ---------------- render ---------------- */

  if (loading && !data) {
    return (
      <div className="page superShopsPage superShopOrdersPage">
        <main className="main">
          <PageHeader title="Orders" />
          <MetricsSkeleton count={4} />
          <TableSkeleton rows={6} columns={7} />
        </main>
      </div>
    );
  }

  if (error) {
    return <ErrorPage />;
  }

  return (
    <div className="page superShopsPage superShopOrdersPage">
      <main className="main">
        <PageHeader
          title="Orders"
          count={totalCount}
          description={
            shopTitle
              ? `Every order placed with ${shopTitle}, and what Spark earned on it.`
              : "Every order placed with this shop, and what Spark earned on it."
          }
          actions={
            <PageActionButton
              icon={<CloudDownloadIcon fontSize="small" />}
              label={exporting ? "Exporting" : "Export"}
              info={exporting ? "Preparing file" : `${totalCount} order${totalCount === 1 ? "" : "s"}`}
              onClick={handleExport}
              disabled={totalCount === 0}
              loading={exporting}
            />
          }
        />

        <section className="stats">
          <MetricCard icon={<ShoppingCartIcon />} title="Matching orders" value={totalCount} />
          <MetricCard
            icon={<HourglassEmptyIcon />}
            title="Still open"
            value={counts.open || 0}
            tone={counts.open > 0 ? "warning" : "default"}
          />
          <MetricCard
            icon={<BlockIcon />}
            title="Rejected"
            value={counts.rejected || 0}
            tone={counts.rejected > 0 ? "danger" : "default"}
          />
          <MetricCard
            icon={<AttachMoneyIcon />}
            title="Commission"
            value={money(pageCommission)}
            hint="On this page"
            tone="success"
          />
        </section>

        <section className="filters">
          <SearchField
            placeholder="Search by customer name or phone number..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <div className="filtersSelects">
            <FilterDropdown
              label={statusFilter ? `Status: ${STATUS_LABELS[statusFilter]}` : "Status"}
              options={[{ value: "", label: "All statuses" }, ...STATUS_OPTIONS]}
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            />
            <FilterDropdown
              label={progressFilter ? PROGRESS_LABELS[progressFilter] : "Progress"}
              options={PROGRESS_OPTIONS}
              value={progressFilter}
              onChange={(e) => setProgressFilter(e.target.value)}
            />
            <NumberRangeFilter
              label="Total"
              fromValue={priceFrom}
              toValue={priceTo}
              onFromChange={(e) => setPriceFrom(e.target.value)}
              onToChange={(e) => setPriceTo(e.target.value)}
            />
            <DateRangeFilter
              label="Placed"
              fromValue={dateFrom}
              toValue={dateTo}
              onFromChange={(e) => setDateFrom(e.target.value)}
              onToChange={(e) => setDateTo(e.target.value)}
            />
            <FilterDropdown
              label={`Sort: ${SORT_OPTIONS.find((o) => o.value === sortOption).label}`}
              options={SORT_OPTIONS}
              value={sortOption}
              active={false}
              align="right"
              onChange={(e) => setSortOption(e.target.value)}
            />
          </div>
        </section>

        {activeFilterChips.length > 0 && (
          <ActiveFilterChipList
            filters={activeFilterChips}
            onClearFilter={clearFilter}
            onClearAll={clearAllFilters}
          />
        )}

        <div className="tableTopBar">
          <div className="tableTopBarLeft">
            {orders.length > 0 && (
              <span className="superShopPill">This page: {money(pageRevenue)}</span>
            )}
          </div>
          <div className="tableTopBarRight">
            <IconButton
              className="tableTopIconButton"
              size="small"
              onClick={() => {
                refetch();
                refetchCounts();
              }}
              title="Refresh orders"
            >
              <RefreshIcon fontSize="small" />
            </IconButton>
          </div>
        </div>

        <div className={`tableResponsive ${loading && data ? "isRefetching" : ""}`}>
          {loading && data && <span className="superShopsSpinner" aria-hidden="true" />}
          <section className="table">
            <div className="tableHeader">
              <div className="tableHeaderCell">Order</div>
              <div className="tableHeaderCell">Customer</div>
              <div className="tableHeaderCell">Items</div>
              <div className="tableHeaderCell">Status</div>
              <div className="tableHeaderCell">Total</div>
              <div className="tableHeaderCell">Placed</div>
              <div className="tableHeaderCell tableHeaderAction">Actions</div>
            </div>

            {orders.length === 0 ? (
              <EmptyState
                icon={<ShoppingCartIcon />}
                title={activeFilterChips.length === 0 ? "No orders yet" : "No matches"}
                message={
                  activeFilterChips.length === 0
                    ? "Orders placed with this shop will show up here."
                    : "No orders match these filters. Try widening or clearing them."
                }
              />
            ) : (
              orders.map((order) => {
                const trackId = order.tracking && order.tracking.trackId;
                const items = ((order.cart && order.cart.products) || []).reduce(
                  (sum, p) => sum + (p.quantity || 0),
                  0
                );
                return (
                  <div className="row" key={order._id}>
                    <div className="superShopStack" data-label="Order">
                      {trackId ? (
                        <span className="superShopValueRow">
                          <span className="superShopMono">{trackId}</span>
                          <IconButton
                            size="small"
                            className="rowActionButton"
                            onClick={() => handleCopy(trackId)}
                            title="Copy track ID"
                          >
                            {copiedId === trackId ? (
                              <CheckCircleOutlineIcon fontSize="small" />
                            ) : (
                              <FileCopyIcon style={{ fontSize: 16 }} />
                            )}
                          </IconButton>
                        </span>
                      ) : (
                        <span className="superShopMuted">No track ID</span>
                      )}
                      <span className="superShopTag">
                        {SHIPMENT_LABELS[order.shipmentModel] || order.shipmentModel || "-"}
                      </span>
                    </div>

                    <div className="superShopPerson" data-label="Customer">
                      {order.user ? (
                        <React.Fragment>
                          <span className="superShopPersonName">{order.user.fullName}</span>
                          <span className="superShopPersonMeta">{order.user.phoneNumber}</span>
                        </React.Fragment>
                      ) : (
                        <span className="superShopMuted">Unknown</span>
                      )}
                    </div>

                    <div className="superShopPerson" data-label="Items">
                      <span className="superShopPersonName">
                        {items} item{items === 1 ? "" : "s"}
                      </span>
                      <span className="superShopPersonMeta">
                        Commission {money(order.commission)}
                      </span>
                    </div>

                    <span data-label="Status">
                      <StatusBadge
                        status={STATUS_TONES[order.status] || "neutral"}
                        label={STATUS_LABELS[order.status] || order.status}
                      />
                    </span>

                    <span className="superShopAmount" data-label="Total">
                      {money(order.finalPrice)}
                    </span>

                    <span className="updated" data-label="Placed">
                      <strong>{moment(order.createdAt).format("MMM D, YYYY")}</strong>
                      <span>{moment(order.createdAt).format("hh:mm A")}</span>
                    </span>

                    <div className="superShopActions">
                      <IconButton
                        size="small"
                        className="rowActionButton"
                        onClick={() => setOrderDetail(order)}
                        title="Order details"
                      >
                        <InfoOutlinedIcon fontSize="small" />
                      </IconButton>
                    </div>
                  </div>
                );
              })
            )}
          </section>
        </div>

        {totalCount > 0 && (
          <PaginationComponent
            currentPage={pageCount}
            pageSize={perPage}
            totalCount={totalCount}
            pageSizeOptions={[10, 20, 50]}
            onPageChange={(newPage) => setPageCount(newPage)}
            onPageSizeChange={(size) => {
              setPerPage(parseInt(size, 10));
              setPageCount(0);
            }}
          />
        )}
      </main>

      <DialogShell
        open={Boolean(orderDetail)}
        onClose={() => setOrderDetail(null)}
        title="Order details"
        subtitle={
          orderDetail
            ? (orderDetail.tracking && orderDetail.tracking.trackId) ||
              `#${orderDetail._id.slice(-8).toUpperCase()}`
            : ""
        }
      >
        {orderDetail && <OrderDetails order={orderDetail} />}
      </DialogShell>
    </div>
  );
};

export default SuperOrder;
